// src/components/Sources/SourceView.jsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from '../../store/useStore';
import { logsApi } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import LogRow from '../Logs/LogRow';
import { SourceConfig } from './SourceConfig';
import { Activity, AlertTriangle, Server, Clock, TrendingUp, Wifi } from 'lucide-react';

// ── Source metadata ───────────────────────────────────────────────────────────
const SOURCE_META = {
  gcp: {
    icon: '☁️', label: 'GCP Cloud Logging', color: '#4285f4',
    sourceKey: 'GCP',
    services: ['cloud-run/api-gateway', 'bigquery/analytics', 'pubsub/orders-sub', 'cloud-sql/prod-db', 'gke/backend'],
    region: 'us-central1, europe-west1',
  },
  azure: {
    icon: '🔷', label: 'Azure Monitor', color: '#0078d4',
    sourceKey: 'AZURE',
    services: ['AKS/nodepool1', 'AzureSQL/prod-db', 'KeyVault/prod-kv', 'EventHub/orders', 'AppService/api'],
    region: 'East US 2, West Europe',
  },
  mongodb: {
    icon: '🍃', label: 'MongoDB Atlas', color: '#22c55e',
    sourceKey: 'MONGODB',
    services: ['mongodb/prod.events', 'mongodb/prod.orders', 'mongodb/prod.sessions'],
    region: 'Atlas / AWS us-east-1',
  },
  database: {
    icon: '🗄️', label: 'PostgreSQL Cluster', color: '#a78bfa',
    sourceKey: 'DATABASE',
    services: ['postgresql/prod-pg-01', 'postgresql/prod-pg-02', 'postgresql/slow-queries', 'postgresql/locks'],
    region: 'prod-pg-01 (primary), prod-pg-02 (standby)',
  },
};

const TABS = ['Overview', 'Configure'];

// ── Demo data generators ──────────────────────────────────────────────────────
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = arr => arr[Math.floor(Math.random() * arr.length)];
const uid  = () => Math.random().toString(36).slice(2, 10);

function makeTimestamp(minutesAgo) {
  return new Date(Date.now() - minutesAgo * 60000 - rand(0, 59) * 1000).toISOString();
}

const DEMO_MESSAGES = {
  AZURE: {
    INFO: [
      'AKS node autoscaler scaled nodepool1 from 3 → 5 nodes',
      'Azure SQL DTU consumption: 42% — within threshold',
      'EventHub consumer group "orders-cg" checkpoint offset 8841923',
      'KeyVault secret rotation completed for prod-api-key',
      'AppService health check passed — /healthz → 200 OK (12ms)',
      'Load balancer distributed 4,201 requests across 5 backends',
      'Log Analytics workspace ingestion rate: 2.4 GB/h',
      'Service Bus queue "notifications" depth: 14 messages',
      'Azure AD token refreshed for service principal app-prod-01',
      'Container registry image pull: myapp:1.4.2 (142 MB)',
      'Virtual machine prod-vm-02 started successfully',
      'AKS pod api-deployment-7f9 scheduled on node aks-nodepool1-3',
      'Application Insights telemetry flushed — 1,204 events/min',
      'Traffic Manager routing to East US 2 (latency: 8ms)',
    ],
    WARN: [
      'AKS pod api-deployment-6b4 restarted (OOMKilled) — memory limit 512Mi exceeded',
      'Azure SQL DTU at 81% — approaching throttle threshold (85%)',
      'EventHub namespace throughput approaching 80% of provisioned units',
      'KeyVault request rate 240 req/10s — close to 2000/10s limit',
      'AppService response time p95 elevated: 1,840ms (threshold: 1,500ms)',
      'Service Bus dead letter queue has 23 messages — investigation needed',
      'AKS PersistentVolumeClaim disk usage 78% on prod-data-pvc',
      'Azure CDN cache-hit ratio dropped to 61% (baseline: 82%)',
      'Cosmos DB RU consumption at 87% — scale-up recommended',
      'Application Gateway SSL certificate expires in 14 days',
    ],
    ERROR: [
      'AKS pod payments-service-5d7 CrashLoopBackOff — exit code 137',
      'Azure SQL connection pool exhausted: max_connections=200 reached',
      'EventHub Kafka consumer group rebalance failed — coordinator unreachable',
      'KeyVault access denied: service principal lacks "secrets/get" permission',
      'AppService deployment slot swap failed — health check timeout after 300s',
      'AKS node aks-nodepool1-2 NotReady — kubelet stopped posting status',
      'Azure Blob Storage 503 ServiceUnavailable — storage account rate limited',
      'Function App execution failed: unhandled exception in OrderProcessor',
    ],
    CRITICAL: [
      'AKS control plane API server unreachable — cluster management disabled',
      'Azure SQL primary database failover initiated — RPO: 0 data loss',
      'Production Event Hub namespace throttled — all producers blocked',
    ],
  },
  GCP: {
    INFO: [
      'Cloud Run service api-gateway autoscaled 2 → 8 instances (concurrency: 80)',
      'BigQuery job bqjob_analytics_daily completed — 2.1 TB processed in 34s',
      'Pub/Sub subscription orders-sub delivered 12,401 messages to consumers',
      'Cloud SQL prod-db query cache hit ratio: 94.2%',
      'GKE node pool autoscaler added 2 nodes (total: 7)',
      'Cloud Load Balancing distributed 9,802 requests — avg latency 23ms',
      'Cloud Armor WAF blocked 14 suspicious requests (CVE-pattern match)',
      'Cloud DNS query resolved prod.api.example.com → 10.0.1.5',
      'GKE cluster prod-gke-01 upgraded to 1.28.4-gke.1083001',
      'Dataflow job streaming-pipeline processed 1.2M events in last minute',
      'Cloud Spanner instance prod-spanner CPU utilization: 28%',
      'Secret Manager version rotation completed for db-password-prod',
    ],
    WARN: [
      'Cloud Run api-gateway instance memory at 78% (limit: 512Mi)',
      'BigQuery slot utilization 84% — query queue forming',
      'Pub/Sub subscription orders-sub backlog: 48,203 unacked messages',
      'Cloud SQL prod-db connections at 180/250 — connection pooling advised',
      'GKE pod backend-7f8 restarted 3 times in the last hour',
      'Cloud Storage bucket prod-data-backup access frequency spike (+340%)',
      'Cloud CDN cache-miss rate elevated: 38% (baseline: 12%)',
    ],
    ERROR: [
      'Cloud Run api-gateway request timeout: /v2/checkout exceeded 60s',
      'BigQuery query failed: Resources exceeded during query execution',
      'Pub/Sub push subscription delivery failed — endpoint 503 after 3 retries',
      'Cloud SQL prod-db: replication lag 12.4s — standby falling behind',
      'GKE pod payments-5c9 OOMKilled — increase memory limit from 256Mi',
      'Cloud Functions order-processor exceeded max instances (1000)',
    ],
    CRITICAL: [
      'GKE control plane unreachable — zone us-central1-c outage',
      'Cloud SQL prod-db primary failed — automated failover to standby in progress',
    ],
  },
  MONGODB: {
    INFO: [
      'Collection prod.events index scan: {userId: 1, createdAt: -1} — 0.8ms',
      'Replica set PRIMARY heartbeat received from secondary rs0:27018',
      'Change stream cursor opened on prod.orders — watching insert/update',
      'Atlas backup snapshot completed — 14.2 GB, RPO: 0 minutes',
      'WiredTiger cache eviction rate: 3.1% — within normal range',
      'Aggregate pipeline prod.sessions $lookup completed: 1,204 docs in 4ms',
      'Index build completed: prod.orders {customerId:1, status:1} — 2.1M docs',
      'Connection pool: 42 active / 200 max — healthy',
      'MongoDB Atlas cluster tier: M30 (4 vCPU / 16 GB RAM) — CPU 22%',
    ],
    WARN: [
      'Slow query detected: prod.orders find({}) — 1,842ms (threshold: 100ms)',
      'WiredTiger cache dirty pages at 19% — approaching 20% eviction trigger',
      'Replica set secondary rs0:27019 replication lag: 8.2s',
      'Collection prod.sessions document size approaching 16MB BSON limit',
      'Index cache hit ratio dropped to 72% — memory pressure',
      'Change stream resume token invalidated — cursor must be recreated',
      'Connection pool utilization 82% (164/200) — scale up recommended',
    ],
    ERROR: [
      'Slow query killed: prod.events aggregate — exceeded 30s oplog window',
      'Replication error: SECONDARY rs0:27020 too stale — needs full resync',
      'Write conflict on prod.orders document {_id: ObjectId("...")} — 3 retries',
      'Atlas cluster M30 disk IOPS limit reached — writes queuing',
      'Change stream error: ChangeStreamHistoryLost — oplog rolled over',
    ],
    CRITICAL: [
      'Atlas PRIMARY failover: rs0:27017 stepdown — election in progress',
      'Disk utilization 95% on Atlas cluster — writes may be rejected',
    ],
  },
  DATABASE: {
    INFO: [
      'pg_stat_activity: 42 active connections / 200 max',
      'Autovacuum completed on prod.orders — 12,401 dead tuples removed',
      'Checkpoint completed: 0.4s write time, 142 MB WAL',
      'Streaming replication lag prod-pg-02: 0.3s — healthy',
      'Index scan on orders(customer_id, created_at) — 0.6ms, 142 rows',
      'pg_stat_statements reset: capturing fresh query fingerprints',
      'WAL sender confirmed standby at LSN 5/A2C03F28',
      'Parallel query used: prod.analytics aggregate — 4 workers, 2.1s',
      'Connection pool PgBouncer: 142 clients / 200 pool size',
      'pg_partman partition prod.events_2026_06 created for next month',
    ],
    WARN: [
      'Slow query: SELECT * FROM prod.orders WHERE status=$1 — 2,840ms (seq scan)',
      'Table prod.sessions bloat ratio: 38% — VACUUM FULL recommended',
      'Autovacuum blocked on prod.events by long-running transaction (42 min)',
      'Lock wait: UPDATE prod.orders PID 18421 waiting on PID 18308 (11s)',
      'WAL archive queue depth: 14 segments — archiver falling behind',
      'pg_stat_activity: 14 idle-in-transaction connections (>5 min)',
      'Checkpoint completion target exceeded: 0.9s / 0.5s target',
    ],
    ERROR: [
      'Deadlock detected: transactions T1(orders) ↔ T2(inventory) — T2 rolled back',
      'Out of shared_buffers: query plan changed from index scan to seqscan',
      'Streaming replication disconnected: prod-pg-02 unreachable (TCP timeout)',
      'pg_logical replication slot logical_slot_01 lag: 1.2 GB — disk risk',
      'Connection refused: max_connections=200 reached — clients queuing',
    ],
    CRITICAL: [
      'Primary prod-pg-01 out of disk space — all writes suspended',
      'WAL corruption detected at LSN 4/B1A02C10 — immediate backup restore required',
    ],
  },
};

function generateDemoLogs(sourceKey, count = 60) {
  const msgs = DEMO_MESSAGES[sourceKey] || DEMO_MESSAGES.AZURE;
  const meta = Object.values(SOURCE_META).find(m => m.sourceKey === sourceKey);
  const levels = ['INFO', 'INFO', 'INFO', 'INFO', 'WARN', 'WARN', 'ERROR', 'CRITICAL'];
  const logs = [];
  for (let i = 0; i < count; i++) {
    const level   = pick(levels);
    const service = pick(meta?.services || ['unknown/service']);
    const message = pick(msgs[level] || msgs.INFO);
    logs.push({
      _id:       `demo-${sourceKey}-${uid()}`,
      level,
      source:    sourceKey,
      service,
      message,
      timestamp: makeTimestamp(i * (60 / count)),
      metadata:  {
        demoData:    true,
        host:        `${service.split('/')[0]}-prod-${rand(1, 4)}`,
        requestId:   uid(),
        durationMs:  rand(2, 3800),
        statusCode:  level === 'ERROR' ? pick([500, 503, 502]) : level === 'WARN' ? pick([429, 408]) : 200,
        region:      meta?.region?.split(',')[0]?.trim() || 'us-east-1',
      },
    });
  }
  return logs;
}

function generateDemoVolume(color) {
  const now = Date.now();
  return Array.from({ length: 12 }, (_, i) => {
    const base = rand(180, 480);
    return {
      t: new Date(now - (11 - i) * 5 * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      total:  base,
      errors: rand(2, Math.floor(base * 0.12)),
      warns:  rand(5, Math.floor(base * 0.18)),
    };
  });
}

// ── Service stats helper ──────────────────────────────────────────────────────
function buildServiceStats(logs, services) {
  const map = {};
  services.forEach(s => { map[s] = { total: 0, errors: 0, warns: 0, avgMs: 0, msTotal: 0 }; });
  logs.forEach(l => {
    const key = services.find(s => s === l.service) || l.service;
    if (!map[key]) map[key] = { total: 0, errors: 0, warns: 0, avgMs: 0, msTotal: 0 };
    map[key].total++;
    if (l.level === 'ERROR' || l.level === 'CRITICAL') map[key].errors++;
    if (l.level === 'WARN' || l.level === 'WARNING') map[key].warns++;
    if (l.metadata?.durationMs) { map[key].msTotal += l.metadata.durationMs; }
  });
  return Object.entries(map).map(([svc, st]) => ({
    svc,
    total: st.total,
    errors: st.errors,
    warns: st.warns,
    avgMs: st.total > 0 ? Math.round(st.msTotal / st.total) : 0,
  })).sort((a, b) => b.total - a.total);
}

export default function SourceView() {
  const { type } = useParams();
  const meta = SOURCE_META[type] || SOURCE_META.gcp;
  const allLogs = useStore(s => s.logs);
  const sources = useStore(s => s.sources);

  const [sourceLogs, setSourceLogs] = useState([]);
  const [volume,     setVolume]     = useState([]);
  const [activeTab,  setActiveTab]  = useState('Overview');

  const filteredLogs = allLogs.filter(l => l.source === meta.sourceKey).slice(0, 100);

  useEffect(() => {
    setActiveTab('Overview');

    logsApi.list({ source: meta.sourceKey, since: 60, limit: 200 })
      .then(d => {
        const logs = d.logs || [];
        setSourceLogs(logs.length > 0 ? logs : generateDemoLogs(meta.sourceKey, 60));
      })
      .catch(() => setSourceLogs(generateDemoLogs(meta.sourceKey, 60)));

    logsApi.volume({ source: meta.sourceKey, minutes: 60, bucket: 5 })
      .then(v => {
        const mapped = (v || []).map(b => ({
          t:      new Date(b._id).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          total:  b.total,
          errors: b.errors,
          warns:  b.warns || 0,
        }));
        setVolume(mapped.length > 0 ? mapped : generateDemoVolume(meta.color));
      })
      .catch(() => setVolume(generateDemoVolume(meta.color)));
  }, [type]);

  const displayLogs = [...sourceLogs, ...filteredLogs]
    .filter((l, i, arr) => arr.findIndex(x => x._id === l._id) === i)
    .slice(0, 80);

  const errorCount = displayLogs.filter(l => l.level === 'ERROR' || l.level === 'CRITICAL').length;
  const warnCount  = displayLogs.filter(l => l.level === 'WARN'  || l.level === 'WARNING').length;
  const avgMs      = displayLogs.length > 0
    ? Math.round(displayLogs.reduce((s, l) => s + (l.metadata?.durationMs || 0), 0) / displayLogs.length)
    : 0;

  const sourceStatus = sources.find(s => s.type === meta.sourceKey);
  const statusColor  = sourceStatus?.status === 'CONNECTED'    ? '#22c55e'
                     : sourceStatus?.status === 'DEGRADED'     ? '#f59e0b'
                     : sourceStatus?.status === 'DISCONNECTED' ? '#ef4444'
                     : '#22c55e';

  const serviceStats = buildServiceStats(displayLogs, meta.services);

  const totalEventsVol = volume.reduce((s, b) => s + b.total,  0);
  const totalErrorsVol = volume.reduce((s, b) => s + b.errors, 0);
  const errPct = totalEventsVol > 0 ? ((totalErrorsVol / totalEventsVol) * 100).toFixed(1) : '0.0';

  return (
    <div className="flex flex-col gap-0 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <span className="text-2xl">{meta.icon}</span>
        <div>
          <div className="font-semibold text-sm">{meta.label}</div>
          <div className="text-xs" style={{ color: 'var(--text-3)' }}>{meta.region}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
            style={{ background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}40` }}>
            <span className="pulse-dot" style={{ background: statusColor }} />
            {sourceStatus?.status || 'Connected'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 18px', fontSize: 12,
              fontWeight: activeTab === tab ? 600 : 400,
              color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
              background: 'none', border: 'none',
              borderBottom: activeTab === tab ? `2px solid ${meta.color}` : '2px solid transparent',
              cursor: 'pointer', transition: 'all .15s',
            }}>
            {tab}
          </button>
        ))}
      </div>

      {/* ── Overview ─────────────────────────────────────────────────────────── */}
      {activeTab === 'Overview' && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 p-4 pb-0"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
            {[
              { label: 'Total Events (1h)', value: displayLogs.length.toLocaleString(), color: meta.color,   icon: Activity },
              { label: 'Errors',            value: errorCount,                           color: '#ef4444',    icon: AlertTriangle },
              { label: 'Warnings',          value: warnCount,                            color: '#f59e0b',    icon: AlertTriangle },
              { label: 'Active Services',   value: meta.services.length,                color: '#a78bfa',    icon: Server },
              { label: 'Avg Latency',       value: avgMs > 0 ? `${avgMs}ms` : '—',      color: '#22d3ee',    icon: Clock },
              { label: 'Error Rate',        value: `${errPct}%`,                        color: errPct >= 5 ? '#f87171' : '#4ade80', icon: TrendingUp },
            ].map(k => {
              const Icon = k.icon;
              return (
                <div key={k.label}
                  style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10 }}
                  className="p-4">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider mb-2"
                    style={{ color: 'var(--text-3)', fontSize: 10, letterSpacing: '1px' }}>
                    <Icon size={10} style={{ color: k.color }} /> {k.label}
                  </div>
                  <div className="mono text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
                </div>
              );
            })}
          </div>

          {/* Volume chart */}
          <div className="px-4 pt-4">
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10 }} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold" style={{ color: 'var(--text-2)' }}>Event Volume (1h)</div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1"><span style={{ width:8, height:8, borderRadius:2, background:`${meta.color}70`, display:'inline-block' }}/> Total</span>
                  <span className="flex items-center gap-1"><span style={{ width:8, height:8, borderRadius:2, background:'#f59e0b80', display:'inline-block' }}/> Warns</span>
                  <span className="flex items-center gap-1"><span style={{ width:8, height:8, borderRadius:2, background:'#ef444460', display:'inline-block' }}/> Errors</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={130}>
                <BarChart data={volume} barCategoryGap="25%">
                  <XAxis dataKey="t"  tick={{ fill: '#64748b', fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis             tick={{ fill: '#64748b', fontSize: 9 }} axisLine={false} tickLine={false} width={32} />
                  <Tooltip contentStyle={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }} />
                  <Bar dataKey="total"  fill={`${meta.color}50`} radius={[2, 2, 0, 0]} name="Total" />
                  <Bar dataKey="warns"  fill="#f59e0b60"         radius={[2, 2, 0, 0]} name="Warnings" />
                  <Bar dataKey="errors" fill="#ef444460"         radius={[2, 2, 0, 0]} name="Errors" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Service breakdown */}
          <div className="px-4 pt-4">
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10 }} className="overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
                <span className="text-xs font-semibold" style={{ color: 'var(--text-2)' }}>Service Breakdown</span>
                <span className="text-xs" style={{ color: 'var(--text-3)' }}>{serviceStats.length} services</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    {['Service', 'Events', 'Errors', 'Warnings', 'Avg Latency', 'Health'].map(h => (
                      <th key={h} style={{ padding: '6px 12px', textAlign: 'left', fontSize: 10, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {serviceStats.map((row, i) => {
                    const health = row.errors === 0 ? 'healthy' : row.errors <= 2 ? 'degraded' : 'critical';
                    const healthColor = health === 'healthy' ? '#22c55e' : health === 'degraded' ? '#f59e0b' : '#ef4444';
                    return (
                      <tr key={row.svc} style={{ borderBottom: '1px solid rgba(255,255,255,.03)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.01)' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <div className="flex items-center gap-2">
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: meta.color, display:'inline-block', flexShrink:0 }} />
                            <span className="mono text-xs" style={{ color: 'var(--text-2)' }}>{row.svc}</span>
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: meta.color, fontWeight: 600 }}>{row.total.toLocaleString()}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: row.errors > 0 ? '#f87171' : 'var(--text-3)' }}>{row.errors}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: row.warns > 0 ? '#fbbf24' : 'var(--text-3)' }}>{row.warns}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: 'var(--text-2)' }}>{row.avgMs > 0 ? `${row.avgMs}ms` : '—'}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: `${healthColor}15`, color: healthColor, border: `1px solid ${healthColor}30` }}>
                            {health}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Log stream */}
          <div className="px-4 pt-4">
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-2)' }}>
              Live Log Stream — {meta.label}
            </div>
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}>
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <span className="mono text-xs" style={{ color: 'var(--text-3)' }}>stream://{type}</span>
                <span className="ml-2 text-xs mono" style={{ color: 'var(--text-3)' }}>{displayLogs.length} entries</span>
                <div className="ml-auto flex items-center gap-1 text-xs" style={{ color: '#22c55e' }}>
                  <span className="pulse-dot" />LIVE
                </div>
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {displayLogs.map((l, i) => <LogRow key={l._id || i} log={l} />)}
                {displayLogs.length === 0 && (
                  <div className="p-6 text-center mono text-xs" style={{ color: 'var(--text-3)' }}>
                    Waiting for {meta.label} events…
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Configure ────────────────────────────────────────────────────────── */}
      {activeTab === 'Configure' && (
        <div style={{ paddingTop: 16 }}>
          <div className="px-4 pb-2">
            <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-2)' }}>Connection Settings</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
              Changes saved here update the runtime config and take effect on the next polling cycle.
            </div>
          </div>
          <SourceConfig sourceType={meta.sourceKey} />
        </div>
      )}
    </div>
  );
}
